import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import {COLORS} from '../../../../styles/styleConstants';

const Field = ({label, value}) => (
  <Box sx={{minWidth: 140}}>
    <Typography sx={{color: COLORS.fontPale, fontSize: '0.75rem', textTransform: 'uppercase'}}>
      {label}
    </Typography>
    <Typography sx={{fontSize: '0.9rem', wordBreak: 'break-word'}}>{value || '-'}</Typography>
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

Field.defaultProps = {
  value: null,
};

/**
 * What the AI heard: the dialer's wrap-up and the full transcript, so an agent can decide what to
 * do with a call without leaving the queue.
 */
const CallDetailsDialog = ({open, call, onClose}) => {
  const name = `${call?.first_name || ''} ${call?.last_name || ''}`.trim();
  return (
    <Dialog open={open} onClose={onClose} maxWidth={'md'} fullWidth scroll={'paper'}>
      <DialogTitle sx={{fontWeight: 700}}>
        {`${name || 'Call'}${call?.jobNumber ? ` — job #${call.jobNumber}` : ''}`}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2}}>
          <Field label={'Phone'} value={call?.phone}/>
          <Field label={'Company'} value={call?.companyName}/>
          <Field label={'Result'} value={call?.result}/>
          <Field label={'Answered by'} value={call?.answeredBy}/>
          <Field label={'Answer'} value={call?.answer}/>
          <Field label={'Reason for call'} value={call?.reason}/>
          <Field label={'Call seq'} value={call?.call_seq}/>
        </Box>
        {call?.dismissedAt && (
          <Typography sx={{fontSize: '0.85rem', mb: 2, color: COLORS.fontPale}}>
            {`Dismissed ${new Date(call.dismissedAt).toLocaleString()}${
              call.dismissedBy ? ` by ${call.dismissedBy}` : ''}${
              call.dismissedNote ? ` — "${call.dismissedNote}"` : ''}`}
          </Typography>
        )}
        <Divider sx={{mb: 2}}/>
        <Typography sx={{fontWeight: 700, mb: 0.5}}>Summary</Typography>
        <Typography sx={{fontSize: '0.9rem', whiteSpace: 'pre-wrap', mb: 2}}>
          {call?.summary || 'No summary was reported for this call.'}
        </Typography>
        <Typography sx={{fontWeight: 700, mb: 0.5}}>Transcript</Typography>
        <Typography
          component={'pre'}
          sx={{
            backgroundColor: COLORS.backgroundMain,
            borderRadius: 1,
            fontFamily: 'inherit',
            fontSize: '0.85rem',
            m: 0,
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {call?.callDetails || 'No transcript was reported for this call.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

CallDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  call: PropTypes.instanceOf(Object),
  onClose: PropTypes.func.isRequired,
};

CallDetailsDialog.defaultProps = {
  call: null,
};

export default CallDetailsDialog;
